import {
    Box,
    Text,
    Heading,
    Picture,
    Button,
} from "@smile2impress/components/relaunch";
import { STRAPI_WEBSITE_API_URL } from "api/cms-website/cms-website";

import Link from "components/Link";
import {
    extractTextFromReactBlockNode,
    ReactBlockNode,
} from "utils/extractTextFromStrapiBlockNode";
import { getImageExtenstion, getImageUrl } from "utils/imageUrl";
import { kebabCase } from "utils/kebabCase";

const smallHeading = {
    fontSize: "16px",
    marginBottom: "14px",
    marginTop: "28px",
} as const;

const headingStyles = {
    1: { fontSize: "26px", marginBottom: "20px", marginTop: "40px" },
    2: { fontSize: "20px", marginBottom: "14px", marginTop: "28px" },
    3: smallHeading,
    4: smallHeading,
    5: smallHeading,
    6: smallHeading,
} as const;

export const blocks = {
    heading: ({
        children,
        level,
    }: {
        children?: React.ReactNode;
        level: 1 | 2 | 3 | 4 | 5 | 6;
    }) => {
        if (!children) {
            return null;
        }

        const childrenArray =
            extractTextFromReactBlockNode(children as ReactBlockNode[]) || "";
        const id = kebabCase(childrenArray);

        const normalizedLevel = headingStyles[level] ? level : 2;
        const styleTag = `h${normalizedLevel}` as const;
        // Cap the semantic tag at h2: the post title is already the page <h1>,
        // so a level-1 body block would create a second <h1> (bad for SEO).
        const semanticTag = normalizedLevel === 1 ? "h2" : styleTag;

        return (
            <Heading
                as={semanticTag}
                variant={styleTag}
                id={id}
                fontWeight="600"
                {...headingStyles[normalizedLevel]}
            >
                {children}
            </Heading>
        );
    },
    paragraph: ({ children }: { children?: React.ReactNode }) =>
        children ? (
            <Text
                margin="15px 0"
                sx={{
                    "> b, strong": {
                        fontWeight: 700,
                    },
                }}
            >
                {children}
            </Text>
        ) : null,
    quote: ({ children }: { children?: React.ReactNode }) =>
        children ? (
            <Text
                margin="40px 0"
                fontWeight="700"
                lineHeight="1"
                fontSize="32px"
                color="#444"
                outline="none"
                _before={{
                    content: '""',
                    display: "block",
                    width: "30%",
                    height: "30px",
                    backgroundColor: "#ff5e58",
                    marginBottom: "30px",
                }}
                _after={{
                    content: '""',
                    display: "block",
                    width: "30%",
                    height: "30px",
                    backgroundColor: "#c8e6ef",
                    marginTop: "30px",
                }}
            >
                {children}
            </Text>
        ) : null,
    image: (props: any) => {
        const imageUrl = getImageUrl(props.image.url, STRAPI_WEBSITE_API_URL);
        const imageExt = getImageExtenstion(imageUrl);

        return (
            <Box as="figure" margin="24px 0">
                <Picture
                    images={
                        imageExt
                            ? [
                                  {
                                      [imageExt]: imageUrl,
                                  },
                              ]
                            : []
                    }
                    defaultSrc={imageUrl}
                    style={{ maxWidth: "100%" }}
                    loading="observer"
                />
            </Box>
        );
    },
    button: ({
        children,
        link,
    }: {
        children?: React.ReactNode;
        link: string;
    }) => {
        return (
            <Box textAlign="center">
                <Button
                    as={Link}
                    href={link}
                    variant="red"
                    size="lg"
                    margin="15px auto"
                >
                    {children}
                </Button>
            </Box>
        );
    },
    embed: ({
        children,
        link,
    }: {
        children?: React.ReactNode;
        link: string;
    }) => {
        const getEmbedUrl = (link: string) => {
            try {
                const url = new URL(link.trim());
                const host = url.hostname.replace(/^www\./, "");
                const pathSegments = url.pathname.split("/").filter(Boolean);

                let videoId: string | undefined;

                if (host === "youtu.be") {
                    videoId = pathSegments[0];
                } else if (host === "youtube.com" || host === "m.youtube.com") {
                    videoId = url.searchParams.get("v") ?? undefined;
                    if (
                        !videoId &&
                        (pathSegments[0] === "embed" ||
                            pathSegments[0] === "shorts")
                    ) {
                        videoId = pathSegments[1];
                    }
                }
                return videoId
                    ? `https://www.youtube.com/embed/${videoId}`
                    : link.trim();
            } catch {
                return link;
            }
        };

        return (
            <Box margin="0 0 15px 0">
                <Box
                    position="relative"
                    width="100%"
                    sx={{ aspectRatio: "16 / 9" }}
                >
                    <Box
                        as="iframe"
                        src={getEmbedUrl(link)}
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        border={0}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                </Box>
                {children && <Text textStyle="body">{children}</Text>}
            </Box>
        );
    },
};
